/** EGB-00006 — Enterprise Data Governance Framework (mirrors pkg/datagovernance) */

export const EGB_DATA_ID = 'EGB-00006';

export interface CouncilMemberRow {
  role: string;
  responsibility: string;
  decisionRights: string;
  reviewCycle: string;
}

export interface PrincipleRow {
  principle: string;
  status: string;
  score: number;
}

export interface MasterDataRow {
  entity: string;
  records: number;
  status: string;
  owner: string;
}

export interface MetadataCatalogRow {
  category: string;
  item: string;
  status: string;
  count: number;
}

export interface LineageRow {
  flow: string;
  source: string;
  target: string;
  status: string;
}

export interface QualityRow {
  dimension: string;
  score: number;
  status: string;
}

export interface LifecyclePrivacyRow {
  area: string;
  policy: string;
  status: string;
}

export interface ExecutiveDashboardRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const COUNCIL_ROLES = [
  'Chief Data Officer', 'Chief Enterprise Architect', 'Chief AI Officer', 'Security & Privacy Officer',
  'Compliance Officer', 'Analytics Lead', 'Marketplace Integration Lead', 'Product Owner',
  'Database Architect', 'Accessibility Specialist',
];

export const COUNCIL_FUNCTIONS = ['Roles', 'Responsibilities', 'Decision Rights', 'Ownership', 'Review Cycles', 'Escalation'];

export const DATA_PRINCIPLES = [
  'Single Source of Truth', 'Master Data', 'Metadata First', 'Data Quality', 'Data Ownership',
  'Privacy by Design', 'Business Glossary', 'Marketplace Agnostic', 'AI Ready',
];

export const MASTER_DATA_ENTITIES = [
  'Organizations', 'Tenants', 'Brands', 'Products', 'SKUs', 'Customers', 'Suppliers', 'Warehouses',
  'Orders', 'Inventory', 'Campaigns', 'Settlements', 'Finance', 'Knowledge', 'AI Agents', 'Prompts', 'DNA',
];

export const METADATA_GOVERNANCE = [
  'Business Metadata', 'Technical Metadata', 'Operational Metadata', 'AI Metadata',
  'Marketplace Metadata', 'API Metadata', 'Schema Metadata',
];

export const DATA_CATALOG = ['Data Assets', 'APIs', 'Events', 'Reports', 'Dashboards', 'Knowledge', 'Owners', 'Consumers'];

export const DATA_LINEAGE = ['Source', 'Transformation', 'Usage', 'AI Consumption', 'Reporting', 'Exports', 'Audit Trail'];

export const QUALITY_DIMENSIONS = [
  'Completeness', 'Accuracy', 'Consistency', 'Validity', 'Uniqueness', 'Timeliness', 'Integrity', 'Business Rule Compliance',
];

export const DATA_LIFECYCLE = [
  'Creation', 'Validation', 'Approval', 'Usage', 'Analytics', 'Sharing', 'Archival', 'Retention', 'Deletion', 'Recovery',
];

export const DATA_PRIVACY = [
  'GDPR', 'DPDP', 'Consent Management', 'Purpose Limitation', 'Anonymization', 'Pseudonymization', 'Retention', 'Deletion',
];

export const AI_DATA_GOVERNANCE = [
  'Training Data', 'Inference Data', 'Prompt Context', 'DNA Context', 'Knowledge Context', 'Evaluation Data', 'Synthetic Data',
];

export const ANALYTICS_GOVERNANCE = ['KPIs', 'Metrics', 'Business Glossary', 'Dashboard Standards', 'Report Certification'];

export const DATA_STEWARDSHIP = ['Owners', 'Custodians', 'Stewards', 'Approvers', 'Quality Reviews', 'Issue Resolution'];

export const DATA_MONITORING = ['Freshness', 'Quality', 'Latency', 'Duplicates', 'Marketplace Synchronization', 'AI Data Readiness'];

export const EXECUTIVE_DASHBOARD = ['Data Health', 'Quality', 'Compliance', 'Privacy', 'Retention', 'Business Risk', 'AI Readiness'];

export const ACCESSIBILITY = ['Accessible Reports', 'Accessible Dashboards', 'Accessible Data Catalog', 'Localization'];

export const ARCHITECTURE_STAGES = [
  'Data Council', 'Data Principles', 'Master Data', 'Metadata & Catalog',
  'Data Lineage', 'Data Quality', 'Lifecycle & Privacy', 'Executive Dashboard',
];

export const LIVE_ROUTES: Record<string, string> = {
  masterConstitution: '/platform/master-constitution',
  analytics: '/analytics',
  knowledge: '/knowledge',
  governance: '/governance',
};

export const DEMO_COUNCIL: CouncilMemberRow[] = [
  { role: 'Chief Data Officer', responsibility: 'Enterprise data strategy & governance', decisionRights: 'Final data governance approval', reviewCycle: 'Weekly' },
  { role: 'Database Architect', responsibility: 'Schema design & data architecture', decisionRights: 'Schema change approval', reviewCycle: 'Bi-weekly' },
  { role: 'Analytics Lead', responsibility: 'KPI standards & report certification', decisionRights: 'Analytics sign-off', reviewCycle: 'Weekly' },
  { role: 'Chief AI Officer', responsibility: 'AI data readiness & training governance', decisionRights: 'AI data approval', reviewCycle: 'Weekly' },
  { role: 'Security & Privacy Officer', responsibility: 'Privacy, retention & anonymization', decisionRights: 'Privacy gate veto', reviewCycle: 'Monthly' },
  { role: 'Marketplace Integration Lead', responsibility: 'Marketplace data synchronization', decisionRights: 'Marketplace data approval', reviewCycle: 'Bi-weekly' },
];

export const DEMO_PRINCIPLES: PrincipleRow[] = [
  { principle: 'Single Source of Truth', status: 'enforced', score: 98 },
  { principle: 'Master Data', status: 'enforced', score: 96 },
  { principle: 'Metadata First', status: 'enforced', score: 95 },
  { principle: 'Data Quality', status: 'active', score: 92 },
  { principle: 'Privacy by Design', status: 'enforced', score: 97 },
  { principle: 'AI Ready', status: 'active', score: 90 },
];

export const DEMO_MASTER_DATA: MasterDataRow[] = [
  { entity: 'Products', records: 12400, status: 'certified', owner: 'Catalog Steward' },
  { entity: 'SKUs', records: 48200, status: 'certified', owner: 'Catalog Steward' },
  { entity: 'Orders', records: 890000, status: 'certified', owner: 'Operations Steward' },
  { entity: 'Inventory', records: 15600, status: 'active', owner: 'Supply Chain Steward' },
  { entity: 'Customers', records: 234000, status: 'certified', owner: 'Customer Steward' },
  { entity: 'AI Agents', records: 48, status: 'active', owner: 'AI Steward' },
  { entity: 'DNA', records: 320, status: 'active', owner: 'Brand Steward' },
];

export const DEMO_METADATA_CATALOG: MetadataCatalogRow[] = [
  { category: 'Metadata', item: 'Business metadata definitions', status: 'current', count: 420 },
  { category: 'Metadata', item: 'Schema metadata entries', status: 'current', count: 186 },
  { category: 'Metadata', item: 'AI metadata records', status: 'active', count: 94 },
  { category: 'Catalog', item: 'Registered data assets', status: 'current', count: 312 },
  { category: 'Catalog', item: 'API endpoints cataloged', status: 'current', count: 148 },
  { category: 'Catalog', item: 'Event definitions', status: 'current', count: 86 },
];

export const DEMO_LINEAGE: LineageRow[] = [
  { flow: 'Amazon SP-API → Catalog', source: 'Marketplace', target: 'Master Data', status: 'tracked' },
  { flow: 'Catalog → Analytics', source: 'Master Data', target: 'ClickHouse', status: 'tracked' },
  { flow: 'Orders → Finance', source: 'Operations', target: 'Settlements', status: 'tracked' },
  { flow: 'Knowledge → AI Agents', source: 'Knowledge Graph', target: 'Inference', status: 'tracked' },
  { flow: 'DNA → Prompt Context', source: 'Brand DNA', target: 'AI Platform', status: 'tracked' },
];

export const DEMO_QUALITY: QualityRow[] = [
  { dimension: 'Completeness', score: 96, status: 'healthy' },
  { dimension: 'Accuracy', score: 94, status: 'healthy' },
  { dimension: 'Consistency', score: 92, status: 'healthy' },
  { dimension: 'Timeliness', score: 89, status: 'monitoring' },
  { dimension: 'Uniqueness', score: 97, status: 'healthy' },
  { dimension: 'Business Rule Compliance', score: 93, status: 'healthy' },
];

export const DEMO_LIFECYCLE_PRIVACY: LifecyclePrivacyRow[] = [
  { area: 'Lifecycle', policy: 'Creation → Validation → Approval → Usage', status: 'enforced' },
  { area: 'Lifecycle', policy: 'Archival after 7 years · Deletion per policy', status: 'policy' },
  { area: 'Privacy', policy: 'GDPR + DPDP compliance', status: 'compliant' },
  { area: 'Privacy', policy: 'Anonymization for analytics exports', status: 'enforced' },
  { area: 'AI Data', policy: 'Training data lineage tracked', status: 'active' },
  { area: 'AI Data', policy: 'Prompt context tenant isolation', status: 'enforced' },
];

export const DEMO_EXECUTIVE: ExecutiveDashboardRow[] = [
  { metric: 'Data Health', value: 'A', status: 'healthy', score: 93 },
  { metric: 'Quality Score', value: '94%', status: 'healthy', score: 94 },
  { metric: 'Compliance', value: 'GDPR + DPDP', status: 'compliant', score: 96 },
  { metric: 'Privacy Posture', value: 'Strong', status: 'healthy', score: 95 },
  { metric: 'AI Readiness', value: '90%', status: 'on_track', score: 90 },
  { metric: 'Marketplace Sync', value: '98% fresh', status: 'healthy', score: 98 },
];

export function buildDataDashboard() {
  return {
    council_roles: COUNCIL_ROLES.length,
    data_principles: DATA_PRINCIPLES.length,
    master_data_entities: MASTER_DATA_ENTITIES.length,
    quality_dimensions: QUALITY_DIMENSIONS.length,
    governance_areas: METADATA_GOVERNANCE.length + DATA_STEWARDSHIP.length,
  };
}
